import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import BACKEND_URL from '../config';
import { ToastContainer, toast } from 'react-toastify';
import DocumentGraph from './DocumentGraph';
import CharacteristicsList from '../components/CharacteristicsList';
import DocumentsList from '../components/DocumentsList';
import { IconLoader2 } from '@tabler/icons-react';
import { Phase, Role } from '../types';

interface Characteristic {
    id: number;
    type: string;
    question_definition: string | null;
    answer_definition: string | null;
    answer_options: string[] | null;
    criterion_of_situation: string | null;
    criterion_of_action: string | null;
    description_value: string | null;
    remarks: string | null;
    comments: string | null;
}

interface Document {
    id: string;
    name: string;
    raw_document_presigned_url: string;
    title: string;
    content: string;
    url: string;
}

interface Documentation {
    id: number;
    document: Document;
    relevance: number;
    characteristic_data: Record<string, any>;
    summary: string;
    name: string;
    phases?: Phase[];
    roles?: Role[];
}

type TabType = 'graph' | 'characteristics' | 'documents';

const DocumentInterpretation = () => {
    const [characteristics, setCharacteristics] = useState<Record<string, Characteristic>>({});
    const [documentation, setDocumentation] = useState<Documentation[]>([]);
    const [loading, setLoading] = useState({
        characteristics: true,
        documentation: false,
    });
    const [allDocsTriggered, setAllDocsTriggered] = useState(false);
    const [activeTab, setActiveTab] = useState<TabType>('documents');

    useEffect(() => {
        fetch(`${BACKEND_URL}/vnog/get_all_characteristics/`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
        })
        .then(response => response.json())
        .then(data => {
            setCharacteristics(data);
            setLoading(prev => ({ ...prev, characteristics: false }));
        })
        .catch(error => {
            console.error('Error fetching characteristics:', error);
            toast.error('Failed to fetch characteristics');
            setLoading(prev => ({ ...prev, characteristics: false }));
        });
    }, []);

    // Only fetch the full documentation set when the Graph or Characteristics
    // tabs are activated. The Documenten tab owns its own paginated fetching.
    useEffect(() => {
        if (activeTab !== 'graph' && activeTab !== 'characteristics') return;
        if (allDocsTriggered) return;
        setAllDocsTriggered(true);
        setLoading(prev => ({ ...prev, documentation: true }));
        const fetchAllDocumentation = async () => {
            let allDocs: Documentation[] = [];
            let url: string | null = `${BACKEND_URL}/vnog/get_all_documentation/?page_size=100`;
            try {
                while (url) {
                    const response: Response = await fetch(url, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'X-CSRFToken': Cookies.get('csrftoken') || '' },
                    });
                    const data: { results?: Documentation[]; next?: string } = await response.json();
                    const results = data.results ?? (data as unknown as Documentation[]);
                    allDocs = [...allDocs, ...results];
                    url = data.next ?? null;
                }
                setDocumentation(allDocs);
            } catch (error) {
                console.error('Error fetching documentation:', error);
                toast.error('Failed to fetch documentation');
            }
            setLoading(prev => ({ ...prev, documentation: false }));
        };
        fetchAllDocumentation();
    }, [activeTab, allDocsTriggered]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'graph':
                return (
                    <div className="bg-white rounded-lg shadow-md p-4">
                        <h2 className="text-xl font-semibold mb-4">Document Relationships</h2>
                        {loading.documentation ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <IconLoader2 className="w-4 h-4 animate-spin" />
                                Loading graph...
                            </div>
                        ) : (
                            <DocumentGraph documentation={documentation} />
                        )}
                    </div>
                );
            case 'characteristics':
                return (
                    <CharacteristicsList
                        characteristics={characteristics}
                        documentation={documentation}
                        loading={loading.characteristics || loading.documentation}
                    />
                );
            case 'documents':
                return (
                    <DocumentsList
                        characteristics={characteristics}
                    />
                );
        }
    };

    return (
        <div className="p-4">
            <ToastContainer />
            {loading.characteristics && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <IconLoader2 className="w-4 h-4 animate-spin" />
                    Laden...
                </div>
            )}
            <div>
                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('characteristics')}
                            className={`${
                                activeTab === 'characteristics'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Meldingsclassificaties (LMC)
                        </button>
                        <button
                            onClick={() => setActiveTab('documents')}
                            className={`${
                                activeTab === 'documents'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Documenten
                        </button>
                        <button
                            onClick={() => setActiveTab('graph')}
                            className={`${
                                activeTab === 'graph'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Associatie Netwerk
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                {renderTabContent()}
            </div>
        </div>
    );
}

export default DocumentInterpretation;